import { WorkflowSelectedListItem } from "@/pages/workflows/components/WorkflowSelectedListItem.tsx";
import { useEmployeeWorkflowRun } from "@/queries/employees/use-employee-workflow.query.ts";
import { useInvalidateEmployeePortalAccess } from "@/queries/preboarding/use-preboarding.query.ts";
import { useWorkflowsSearchInline } from "@/queries/workflows/workflow.query.ts";
import { ApiWorkflowListItem, WorkflowRunStatus } from "@/types/workflows/workflow.types.ts";
import {
  Button,
  Modal,
  ModalProps,
  Select,
  SelectValueType,
  Stack,
  Text,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { ComponentProps, useState } from "react";
import { useTranslation } from "react-i18next";

export interface Props extends ModalProps {
  employeeId: number;
  mode: "add" | "replace";
}

export function EmployeePortalAccessWorkflowsModal({ ...props }: Props) {
  const { employeeId, mode, id } = props;
  const { t } = useTranslation();
  const { close } = useModals();
  const { showInfo } = useToasts();
  const runWorkflow = useEmployeeWorkflowRun();
  const invalidatePortalAccess = useInvalidateEmployeePortalAccess(employeeId);
  const { items, isFetching, search } = useWorkflowsSearchInline();

  const [selected, setSelected] = useState<ApiWorkflowListItem[]>([]);
  const [statuses, setStatuses] = useState<Record<number, WorkflowRunStatus>>({});
  const [isRunning, setIsRunning] = useState(false);

  const selectedIds = new Set(selected.map((workflow) => workflow.id));
  const options: ComponentProps<typeof Select>["options"] = items
    .filter((workflow) => !selectedIds.has(workflow.id))
    .map((workflow) => ({
      id: workflow.id,
      title: workflow.title,
      caption: workflow.folder?.title,
    }));

  const handleSelect = (value: SelectValueType) => {
    const workflowId = typeof value === "number" ? value : undefined;
    if (!workflowId) return;

    const workflow = items.find((item) => item.id === workflowId);
    if (!workflow) return;

    setSelected((prev) => [...prev, workflow]);
    search(undefined);
  };

  const handleRemove = (workflowId: number) => {
    setSelected((prev) => prev.filter((workflow) => workflow.id !== workflowId));
    setStatuses((prev) => {
      const next = { ...prev };
      delete next[workflowId];
      return next;
    });
  };

  const handleRun = async () => {
    if (!selected.length || isRunning) return;

    setIsRunning(true);

    const nextStatuses: Record<number, WorkflowRunStatus> = { ...statuses };
    let hasSuccess = Object.values(nextStatuses).includes("success");

    for (const workflow of selected) {
      if (nextStatuses[workflow.id] === "success") continue;

      try {
        const result = await runWorkflow.mutateAsync({
          employeeId,
          workflowId: workflow.id,
          payload: {
            block_portal_access: true,
            replace_existing_blockers: mode === "replace" && !hasSuccess,
          },
        });

        if (result.blocking_execution_attached) {
          nextStatuses[workflow.id] = "success";
          hasSuccess = true;
        } else {
          nextStatuses[workflow.id] = "error";
        }
      } catch {
        nextStatuses[workflow.id] = "error";
      }
    }

    setStatuses(nextStatuses);
    setIsRunning(false);

    if (hasSuccess) invalidatePortalAccess();

    if (selected.every((workflow) => nextStatuses[workflow.id] === "success")) {
      showInfo({ title: t("core:portal_access_run_success") });
      close(id);
    }
  };

  const hasErrors = selected.some((workflow) => statuses[workflow.id] === "error");

  return (
    <Modal
      {...props}
      title={
        mode === "replace"
          ? t("core:portal_access_replace_workflows")
          : t("core:portal_access_add_workflows")
      }
      variant={"middle"}
      layout={"base"}
      size={"md"}
      withCloser={true}
      actions={
        <Stack gap={"smd"}>
          <Button
            isLoading={isRunning}
            disabled={!selected.length || isRunning}
            onClick={handleRun}
            size={"lg"}
          >
            {hasErrors ? t("common:retry") : t("workflow:run_workflow")}
          </Button>
          <Button size={"md"} variant={"plain"} onClick={() => close(id)}>
            {t("common:cancel")}
          </Button>
        </Stack>
      }
    >
      <Stack gap={"md"}>
        <Text className={"text-secondary"}>
          {mode === "replace"
            ? t("core:portal_access_replace_workflows_hint")
            : t("core:portal_access_add_workflows_hint")}
        </Text>

        <Select
          label={t("workflow:hire_choose_workflow")}
          emptyTitle={t("workflow:hire_choose_workflow")}
          options={options}
          searchOptions={options}
          onSearchOptions={(text) => search(text ?? undefined)}
          value={undefined}
          onChange={handleSelect}
          searchable
          showCaption
          isLoading={isFetching}
        />

        {selected.length > 0 && (
          <Stack>
            {selected.map((workflow) => (
              <WorkflowSelectedListItem
                key={workflow.id}
                workflow={workflow}
                runStatus={statuses[workflow.id]}
                onRemove={handleRemove}
              />
            ))}
          </Stack>
        )}

        {hasErrors && (
          <Text className={"text-danger"}>{t("core:portal_access_partial_error")}</Text>
        )}
      </Stack>
    </Modal>
  );
}
